import { ArrowLeft, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@shared/lib';

/**
 * TRAD-1881 — 상세/작성 화면의 "뒤로" 버튼 단일 규격.
 *
 * ## 왜 만들었나
 * 착수 전 실측: `navigate(-1)` 버튼이 **11곳/10파일**에 손으로 그려져 있었고 규격이 갈렸다.
 *   아이콘 전용 6곳 — 손으로 쓴 `<svg>` (path `M19 12H5M12 19l-7-7 7-7` = lucide ArrowLeft 와 동일)
 *   아이콘+라벨 3곳 — lucide ArrowLeft/ChevronLeft 16px
 *   텍스트 전용 1곳 — 약관 화면
 * 같은 동작(뒤로 가기)에 마크업이 넷이라, 화면마다 크기·간격이 조금씩 달랐다.
 *
 * ## 🔴 이 리팩터링에서 같이 걷어내는 i18n 위반 2종
 *   `<span>뒤로</span>`  (user-profile ×2) — **화면에 보이는 한글 하드코딩**. EN 사용자에게 한글이 그대로 노출됐다.
 *   `aria-label="뒤로 가기"` (complex-detail) — 스크린리더로 읽히는 한글 하드코딩.
 * 둘 다 `common.back` 카탈로그를 거치게 바꾼다. KO 문구는 기존과 같은 "뒤로"라 **시각 변화는 없다**.
 *
 * ## 아이콘 글리프를 하나로 합치지 않은 이유
 * derivatives 만 `ChevronLeft`(꺾쇠), 나머지는 `ArrowLeft`(화살표)다. 합치면 그 화면의 모양이
 * 바뀐다 — 리팩터링에서 UI를 바꾸지 않는다는 원칙에 걸린다. 글리프 통일은 **디자인 결정**이므로
 * `icon` 으로 남겨 두고 별도로 판단받는다.
 */
export type DetailBackButtonIcon = 'arrow' | 'chevron';

/**
 * ~~`lg`(24px) / `sm`(16px) 두 크기~~ — **TRAD-3236 에서 `size` 를 삭제했다.**
 *
 * 🔴 **`size = 'lg'` 가 `onClick ?? navigate(-1)` 과 똑같은 「조용한 기본값」이었다.**
 * 무인자 `<DetailBackButton />` 6곳이 *아무 말도 안 했는데* 24px 로 렌더됐고, 그 6곳이
 * **D-3(목적지) 위반이자 D-4(라벨) 위반이자 lg 렌더**였다 — **세 결함이 아니라 한 결함의 세 얼굴**.
 * `label` 을 필수로 만들면 그 6곳이 전부 컴파일 실패하고 셋이 한 번에 죽는다.
 *
 * 🔴 **파생(size 를 label 유무에서 계산)을 기각한 이유**(designer): *판별 입력이 상수가 되도록
 * 설계된 규칙은 규칙이 아니다.* D-4 가 성공하면 17곳 전부 라벨을 가지므로 파생 결과가 항상
 * `sm` 이 되어 `lg` 분기 사용자가 0 이 된다. **삭제가 파생보다 강하다 — 파생은 잘못된 조합을
 * 못 쓰게 하고, 삭제는 조합 자체를 없앤다.**
 */
const ICON_CLASS = 'w-4 h-4';

/**
 * 🔴 뒤로가기의 **행선지를 타입으로 강제한다** (TRAD-3236, D-3).
 *
 * 종전 기본값 `onClick ?? (() => navigate(-1))` 은 **아무것도 안 넘긴 호출부를 조용히
 * 히스토리 기반으로** 만들었다 — 11곳이 그 상태였고, 소스만 봐서는 「-1 을 고른 것」인지
 * 「아무 말도 안 한 것」인지 구별이 안 됐다.
 *
 * 이제 셋 중 하나를 **반드시** 고른다. 아무것도 안 주면 컴파일이 안 된다:
 *   `to`           목적지 경로. D-3 의 기본 선택 — 검색엔진으로 직접 들어온 사용자가
 *                  뒤로를 눌러도 사이트 밖으로 안 나간다
 *   `onClick`      화면 고유 처리(확인 모달·계산된 목적지 등)
 *   `historyBack`  🔴 **-1 을 「골랐다」는 기록.** 돌아갈 목록이 실재하지 않는 화면용
 *                  (예: 프로필 — 앱 안에서 그 화면으로 가는 링크가 0건이라 「목록으로」가
 *                   성립하지 않는다). **「안 준 것」과 구별되는 것이 이 갈래의 존재 이유다.**
 */
type BackNav =
  | { to: string; onClick?: never; historyBack?: never }
  | { onClick: () => void; to?: never; historyBack?: never }
  | { historyBack: true; to?: never; onClick?: never };

type DetailBackButtonProps = {
  /**
   * 🔴 **필수다** (TRAD-3236, D-4). 아이콘 전용을 타입으로 막는다 —
   * 종전엔 생략하면 아이콘만 남고 접근성 이름만 `common.back` 이 대신했는데,
   * 화면에서는 **어디로 가는지 알 수 없는 화살표**였다.
   */
  label: string;
  icon?: DetailBackButtonIcon;
  className?: string;
} & BackNav;

export const DetailBackButton = ({
  label,
  icon = 'arrow',
  to,
  onClick,
  historyBack,
  className,
}: DetailBackButtonProps) => {
  const navigate = useNavigate();
  const Icon = icon === 'chevron' ? ChevronLeft : ArrowLeft;
  // 🔴 세 갈래는 타입이 배타로 강제하므로 여기서 우선순위 다툼이 없다.
  //    `historyBack` 은 값을 안 쓰고 갈래 선택으로만 쓰인다 — 그래서 조건에서 마지막이다.
  const handleClick = to ? () => navigate(to) : (onClick ?? (() => navigate(-1)));
  void historyBack;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        // 🔴 TRAD-3237 — 터치 타깃 44×44(WCAG 2.5.5 AAA). 아이콘 크기는 그대로 두고 **박스만** 키운다.
        //   `min-h-11`/`min-w-11` = 정확히 44px(11 × 4px). 임의값 `min-h-[44px]` 을 쓰지 않는 이유는
        //   Tailwind JIT 이 소스 스캔으로 만드는 클래스라, 안 만들어지면 **에러 없이 조용히 0** 이 되기
        //   때문이다(③-b `px-25` 사고와 같은 계열). 기본 사다리 안의 값은 그 위험이 없다.
        //   🔴 `min-*` 이라 3236 에서 `size` 가 삭제돼 아이콘이 16 으로 통일돼도 44 는 유지된다 —
        //   고정 패딩(`p-3`)이면 lg 24+24=48 / sm 16+24=40 으로 갈렸다.
        // 🔴 `inline-flex` — 종전 `flex` 는 **블록 레벨**이라 부모 폭을 전부 먹었다. 실측(2026-09-08 dev):
        //   같은 컴포넌트가 polymarket 50.8px · notice 88.3px · **real-estate-community 1280px**.
        //   1280 짜리는 「목록으로」 글자에서 한참 오른쪽, 아무것도 없어 보이는 자리를 눌러도 뒤로 갔다.
        //   `min-*` 만으로는 안 고쳐진다 — min 은 하한이지 상한이 아니다.
        // 🔴 그리고 `inline-flex` 만으로도 안 고쳐진다 — **flex 항목은 blockify 되어 `inline-flex` 가
        //   `flex` 로 계산된다**(실측: `disp=flex`). 그 화면의 부모가 `flex flex-col` + align-items
        //   stretch 라 교차축(=가로)으로 늘어나 **1280 이 그대로였다.** `w-fit` 이 「내용보다 넓어지지
        //   않는다」를 부모 종류(블록·flex-row·flex-col)와 무관하게 말한다. min-w-11 과 함께면
        //   폭 = max(내용, 44) 이다.
        'inline-flex w-fit items-center justify-center min-h-11 min-w-11',
        'text-muted-foreground hover:text-foreground transition-colors',
        'gap-1 text-sm',
        className,
      )}
    >
      <Icon className={ICON_CLASS} />
      <span>{label}</span>
    </button>
  );
};
